/**
 * Add Shareholder Modal
 *
 * Modal for adding a new shareholder to the share book.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Search, Loader2 } from "lucide-react";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { useCreateShareholder } from "@/hooks/use-share-book";
import { companiesApi, type ShareholderType } from "@/lib/api";

interface AddShareholderModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

const shareholderSchema = z.object({
  type: z.enum(["person", "company", "estate", "trust"]),
  name: z.string().min(1, "Namn krävs"),
  personalId: z.string().optional(),
  orgNumber: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  email: z.string().email("Ogiltig e-postadress").optional().or(z.literal("")),
  phone: z.string().optional(),
  hidePersonalId: z.boolean().optional(),
  hasGuardian: z.boolean().optional(),
  guardianName: z.string().optional(),
  guardianPersonalId: z.string().optional(),
  guardianAddress: z.string().optional(),
  notes: z.string().optional(),
});

type ShareholderFormData = z.infer<typeof shareholderSchema>;

const SHAREHOLDER_TYPE_LABELS: Record<ShareholderType, string> = {
  person: "Fysisk person",
  company: "Juridisk person",
  estate: "Dödsbo",
  trust: "Stiftelse/Trust",
};

export function AddShareholderModal({ isOpen, onClose, companyId }: AddShareholderModalProps) {
  const createShareholder = useCreateShareholder();
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ShareholderFormData>({
    resolver: zodResolver(shareholderSchema),
    defaultValues: {
      type: "person",
      name: "",
      personalId: "",
      orgNumber: "",
      address: "",
      postalCode: "",
      city: "",
      country: "Sverige",
      email: "",
      phone: "",
      hidePersonalId: false,
      hasGuardian: false,
      guardianName: "",
      guardianPersonalId: "",
      guardianAddress: "",
      notes: "",
    },
  });

  const shareholderType = watch("type");
  const hasGuardian = watch("hasGuardian");
  const orgNumber = watch("orgNumber");

  // Lookup company from Bolagsverket
  const handleCompanyLookup = async () => {
    if (!orgNumber || orgNumber.length < 10) {
      setLookupError("Ange ett giltigt organisationsnummer");
      return;
    }

    setIsLookingUp(true);
    setLookupError(null);

    try {
      const result = await companiesApi.lookup(orgNumber);

      if (result.success && result.data) {
        const company = result.data;
        setValue("name", company.name);
        if (company.address?.street) setValue("address", company.address.street);
        if (company.address?.postalCode) setValue("postalCode", company.address.postalCode);
        if (company.address?.city) setValue("city", company.address.city);
        // Keep Sweden as default if no country returned
        setLookupError(null);
      } else {
        setLookupError(result.error?.message || "Kunde inte hitta företaget");
      }
    } catch {
      setLookupError("Ett fel uppstod vid sökningen");
    } finally {
      setIsLookingUp(false);
    }
  };

  const onSubmit = async (data: ShareholderFormData) => {
    try {
      await createShareholder.mutateAsync({
        companyId,
        data: {
          type: data.type,
          name: data.name,
          personalId: data.personalId || undefined,
          orgNumber: data.orgNumber || undefined,
          address: data.address || undefined,
          postalCode: data.postalCode || undefined,
          city: data.city || undefined,
          country: data.country || undefined,
          email: data.email || undefined,
          phone: data.phone || undefined,
          hidePersonalId: data.hidePersonalId || false,
          hasGuardian: data.hasGuardian || false,
          guardianName: data.guardianName || undefined,
          guardianPersonalId: data.guardianPersonalId || undefined,
          guardianAddress: data.guardianAddress || undefined,
          notes: data.notes || undefined,
        },
      });
      toast.success("Aktieägaren har lagts till");
      reset();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte lägga till aktieägaren";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setLookupError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Lägg till aktieägare" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Typ av ägare *
          </label>
          <select {...register("type")} className="input">
            {Object.entries(SHAREHOLDER_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Namn *
          </label>
          <input
            type="text"
            {...register("name")}
            className="input"
            placeholder={shareholderType === "person" ? "Förnamn Efternamn" : "Företagsnamn AB"}
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* ID */}
        <div className="grid sm:grid-cols-2 gap-4">
          {shareholderType === "person" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personnummer
              </label>
              <input
                type="text"
                {...register("personalId")}
                className="input"
                placeholder="YYYYMMDD-XXXX"
              />
              <p className="text-xs text-gray-500 mt-1">
                Obligatoriskt enligt ABL 5 kap 2§
              </p>
            </div>
          ) : (
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organisationsnummer
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  {...register("orgNumber")}
                  className="input flex-1"
                  placeholder="XXXXXX-XXXX"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCompanyLookup}
                  disabled={isLookingUp}
                  title="Hämta uppgifter från Bolagsverket"
                >
                  {isLookingUp ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Search size={18} />
                  )}
                </Button>
              </div>
              {lookupError && (
                <p className="text-sm text-red-600 mt-1">{lookupError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Klicka på sökknappen för att hämta uppgifter från Bolagsverket
              </p>
            </div>
          )}
          {shareholderType === "person" && (
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register("hidePersonalId")}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Dölj personnummer i utskrift
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Postadress
          </label>
          <input
            type="text"
            {...register("address")}
            className="input"
            placeholder="Gatuadress"
          />
          <p className="text-xs text-gray-500 mt-1">
            Obligatoriskt enligt ABL 5 kap 2§
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postnummer
            </label>
            <input
              type="text"
              {...register("postalCode")}
              className="input"
              placeholder="123 45"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ort
            </label>
            <input
              type="text"
              {...register("city")}
              className="input"
              placeholder="Stockholm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Land
            </label>
            <input
              type="text"
              {...register("country")}
              className="input"
              placeholder="Sverige"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-post
            </label>
            <input
              type="email"
              {...register("email")}
              className="input"
              placeholder="namn@exempel.se"
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              {...register("phone")}
              className="input"
              placeholder="070-123 45 67"
            />
          </div>
        </div>

        {/* Guardian section (for minors) */}
        {shareholderType === "person" && (
          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                {...register("hasGuardian")}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Aktieägaren har god man/förmyndare
              </span>
            </label>

            {hasGuardian && (
              <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    God man/förmyndare namn
                  </label>
                  <input
                    type="text"
                    {...register("guardianName")}
                    className="input"
                    placeholder="Förnamn Efternamn"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personnummer
                  </label>
                  <input
                    type="text"
                    {...register("guardianPersonalId")}
                    className="input"
                    placeholder="YYYYMMDD-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adress
                  </label>
                  <input
                    type="text"
                    {...register("guardianAddress")}
                    className="input"
                    placeholder="Fullständig postadress"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Anteckningar
          </label>
          <textarea
            {...register("notes")}
            className="input min-h-[80px]"
            placeholder="Interna anteckningar om aktieägaren..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sparar...
              </>
            ) : (
              "Lägg till aktieägare"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
